import {
  Button,
  DatePicker,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Affix,
  Menu,
  Modal,
} from "antd";
import { useState, useEffect, useRef } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useGetReservoirListQuery } from "../../../../../redux/api/reservoir/reservoirApi";
import { useLazyGetReservoirHistoryGroupByListQuery } from "../../../../../redux/api/reservoirHistory/reservoirHistoryApi";
import { EntReservoirFilter } from "../../../../../models/filters/reservoirFilter";
import { ReportReservoirHistoryModal } from "../../../../../pages/admin/reservoirsHistory/ReportReservoirHistoryModal";
import { useReservoirsHistory } from "../../../../../pages/admin/reservoirsHistory/useReservoirsHistory";
import { useNavigate } from 'react-router-dom';
import styles from "./styles.module.css";
import { PreloaderSvg } from "../../../../preloaderSvg/PreloaderSvg";

interface ReservoirData {
  name: string | null;
  data: {
    date: string;
    admission: number;
    workOff: number;
    volume: number;
  }[];
}

interface ReservoirRecord {
  admission: number;
  workOff: number;
  volume: number;
}

interface IProps {
  onReset: () => void;
  onSearch: () => void;
  openAddModal: () => void;
  openReportModal: () => void;
}

export const ReservoirGraphs = (props: IProps) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(30, "day")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [modal, contextHolder] = Modal.useModal();
  const { data: reservoirs } = useGetReservoirListQuery(new EntReservoirFilter());
  const [reservoirData, setReservoirData] = useState<ReservoirData[]>([]);
  const chartsRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [fetchReservoirData, { data: reservoirHistoryData, isLoading }] = useLazyGetReservoirHistoryGroupByListQuery();
  const { onGenerateReservoirReport } = useReservoirsHistory(modal);
  const navigate = useNavigate();
  const handleGoBack = () => {
    navigate(-1); 
  };

  useEffect(() => {
    if (reservoirs?.res?.content && startDate && endDate) {
      fetchReservoirData({
        dateForDayFrom: startDate.format("YYYY-MM-DD"),
        dateForDayTo: endDate.format("YYYY-MM-DD"),
        reservoirId: null,
        deleted: false,
        page: 1,
        size: 100,
      }).then((response) => {
        if (response.data?.res?.content && reservoirs.res?.content) {
          const historyData = response.data.res.content;
          const processedData: ReservoirData[] = [];
          const reservoirContent = reservoirs.res.content;

          historyData.forEach((day) => {
            const keys = Object.keys(day.reservoirs || {}).sort((a, b) => Number(a) - Number(b));
            
            keys.forEach((key) => {
              const index = Number(key) - 1;
              const reservoir = day.reservoirs?.[key as keyof typeof day.reservoirs] as ReservoirRecord;
              
              if (!processedData[index] && reservoirContent[index]) {
                processedData[index] = {
                  name: reservoirContent[index].nameRu || reservoirContent[index].nameKy,
                  data: []
                };
              }
              
              if (processedData[index] && reservoir) {
                processedData[index].data.push({
                  date: day.dateForDay,
                  admission: reservoir.admission || 0,
                  workOff: reservoir.workOff || 0,
                  volume: reservoir.volume || 0,
                });
              }
            });
          });

          setReservoirData(processedData);
        }
      });
    }
  }, [reservoirs, startDate, endDate]);

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date) setStartDate(date);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (date) setEndDate(date);
  };

  const scrollToReservoir = (index: number) => {
    chartsRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <PreloaderSvg />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Row gutter={[24, 0]}>
        <Col span={4}>
          <Affix offsetTop={24}>
            <div className={styles.sidebarContainer}>
              <Button className={styles.arrow_back_main} onClick={handleGoBack} type="primary">
                <img className={styles.arrow_back} src="/Arrow-Left.png" alt="назад" />
                Назад
              </Button>
              <Card size="small" className={styles.datePickerCard}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <div>
                    <Typography.Text strong className={styles.dateLabel}>
                      Начало:
                    </Typography.Text>
                    <DatePicker
                      value={startDate}
                      onChange={handleStartDateChange}
                      format="YYYY-MM-DD"
                      className={styles.datePicker}
                      allowClear={false}
                      size="small"
                      disabledDate={(current) =>
                        endDate ? current > endDate : false
                      }
                    />
                  </div>
                  <div>
                    <Typography.Text strong className={styles.dateLabel}>
                      Конец:
                    </Typography.Text>
                    <DatePicker
                      value={endDate}
                      onChange={handleEndDateChange}
                      format="YYYY-MM-DD"
                      className={styles.datePicker}
                      allowClear={false}
                      size="small"
                      disabledDate={(current) =>
                        startDate ? current < startDate : false
                      }
                    />
                  </div>
                </Space>
              </Card>

              <Button
                type="primary"
                style={{ marginRight: 10 }}
                onClick={() => setShowReportModal(true)}
              >
                Создать отчет
              </Button>

              <Card size="small">
                <Typography.Title level={5} className={styles.navigationTitle}>
                  Навигация
                </Typography.Title>
                <div className={styles.navigationContainer}>
                  <Menu
                    mode="vertical"
                    className={styles.navigationMenu}
                    items={reservoirData.map((reservoir, index) => ({
                      key: index,
                      label: reservoir.name,
                      onClick: () => scrollToReservoir(index),
                    }))}
                  />
                </div>
              </Card>
            </div>
          </Affix>
        </Col>

        <Col span={20}>
          <Typography.Title level={2} className={styles.pageTitle}>
            Графики по Водохранилищам
          </Typography.Title>

          {reservoirData.map((reservoir, index) => (
            <div
              key={index}
              ref={(el) => (chartsRefs.current[index] = el)}
              className={styles.chartContainer}
            >
              <Card title={reservoir.name}>
                <div className={styles.chart}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={reservoir.data}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis
                        dataKey="date"
                        stroke="#666"
                        tick={{ fill: "#666" }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis stroke="#666" tick={{ fill: "#666" }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="admission"
                        stroke="#1890ff"
                        name="Поступление"
                        strokeWidth={2}
                        dot={{ fill: "#1890ff" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="workOff"
                        stroke="#52c41a"
                        name="Сработка"
                        strokeWidth={2}
                        dot={{ fill: "#52c41a" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="volume"
                        stroke="#faad14"
                        name="Объём"
                        strokeWidth={2}
                        dot={{ fill: "#faad14" }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          ))}
        </Col>
      </Row>
      <ReportReservoirHistoryModal
        show={showReportModal}
        reservoirHistory={reservoirData}
        onHide={() => setShowReportModal(false)}
        onSubmit={async (data) => {
          const success = await onGenerateReservoirReport(data);
          if (success) {
            setShowReportModal(false);
          }
          return success;
        }}
      />
      {contextHolder}
    </div>
  );
};
